import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Clock } from "lucide-react";

const DAYS = [["mon", "Mon"], ["tue", "Tue"], ["wed", "Wed"], ["thu", "Thu"], ["fri", "Fri"], ["sat", "Sat"], ["sun", "Sun"]];
const KINDS = [["day", "Day Shift"], ["afternoon", "Afternoon Shift"], ["other", "Other"]];
const emptyDay = { off: false, work_start: "08:00", work_end: "17:00", lunch_start: "12:00", lunch_end: "13:00" };
const emptyGroup = () => ({ name: "", active: true, notes: "", shift_kind: "day", days: Object.fromEntries(DAYS.map(([k]) => [k, { ...emptyDay }])) });

export default function ShiftGroupsTab() {
  const { can } = useAuth();
  const canManage = can("hr:schedule_manage");
  const [groups, setGroups] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyGroup());

  const load = () => api.get("/hr/shift-groups").then(r => setGroups(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openEdit = (g) => {
    setEditing(g);
    const days = Object.fromEntries(DAYS.map(([k]) => [k, { ...emptyDay, ...(g.days?.[k] || {}) }]));
    setForm({ name: g.name, active: g.active, notes: g.notes || "", shift_kind: g.shift_kind || "day", days });
    setOpen(true);
  };
  const setDay = (k, field, v) => setForm(f => ({ ...f, days: { ...f.days, [k]: { ...f.days[k], [field]: v } } }));
  const save = async () => {
    try {
      if (editing) await api.put(`/hr/shift-groups/${editing.id}`, form);
      else await api.post("/hr/shift-groups", form);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Delete shift group?")) return;
    try { await api.delete(`/hr/shift-groups/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  return (
    <div data-testid="hr-shifts-tab">
      <div className="flex items-center justify-between mb-3">
        <p className="text-slate-500 text-sm">Configurable weekly working hours per shift group.</p>
        {canManage && <Button data-testid="shift-new-button" onClick={() => { setEditing(null); setForm(emptyGroup()); setOpen(true); }} className="bg-sky-600 hover:bg-sky-700"><Plus className="w-4 h-4 mr-1.5" />Shift Group</Button>}
      </div>
      <div className="grid grid-cols-2 gap-4">
        {groups.map(g => (
          <div key={g.id} className="rounded-lg border border-slate-200 bg-white p-4" data-testid={`shift-card-${g.id}`}>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold flex items-center gap-2"><Clock className="w-4 h-4 text-sky-600" />{g.name}<span className={`text-[10px] px-1.5 py-0.5 rounded ${g.shift_kind === "afternoon" ? "bg-sky-100 text-sky-700" : "bg-slate-100 text-slate-500"}`}>{g.shift_kind || "day"}</span></h4>
              {canManage && <span className="flex gap-1">
                <button onClick={() => openEdit(g)} data-testid={`shift-edit-${g.id}`}><Pencil className="w-4 h-4 text-slate-400" /></button>
                <button onClick={() => del(g.id)}><Trash2 className="w-4 h-4 text-rose-500" /></button>
              </span>}
            </div>
            <table className="w-full text-xs">
              <tbody>
                {DAYS.map(([k, lbl]) => {
                  const d = g.days?.[k] || {};
                  return <tr key={k} className="border-b border-slate-50 last:border-0">
                    <td className="py-1 font-medium text-slate-500 w-12">{lbl}</td>
                    <td className="py-1">{d.off ? <span className="text-slate-400">Off</span> : `${d.work_start || "—"}–${d.work_end || "—"}`}</td>
                    <td className="py-1 text-slate-400">{!d.off && d.lunch_start ? `lunch ${d.lunch_start}–${d.lunch_end}` : ""}</td>
                  </tr>;
                })}
              </tbody>
            </table>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit shift group" : "New shift group"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Name *</Label><Input data-testid="shift-name-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Shift kind</Label>
              <Select value={form.shift_kind} onValueChange={v => setForm({ ...form, shift_kind: v })}>
                <SelectTrigger data-testid="shift-kind-select"><SelectValue /></SelectTrigger>
                <SelectContent>{KINDS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2 mt-3">
            {DAYS.map(([k, lbl]) => (
              <div key={k} className="flex items-center gap-2 text-sm">
                <span className="w-10 font-medium">{lbl}</span>
                <label className="flex items-center gap-1 text-xs"><Switch checked={!form.days[k].off} onCheckedChange={v => setDay(k, "off", !v)} data-testid={`shift-day-${k}-on`} />Work</label>
                {!form.days[k].off && <>
                  <Input type="time" className="h-8 w-28" value={form.days[k].work_start || ""} onChange={e => setDay(k, "work_start", e.target.value)} />
                  <span>–</span>
                  <Input type="time" className="h-8 w-28" value={form.days[k].work_end || ""} onChange={e => setDay(k, "work_end", e.target.value)} />
                  <span className="text-xs text-slate-400">lunch</span>
                  <Input type="time" className="h-8 w-28" value={form.days[k].lunch_start || ""} onChange={e => setDay(k, "lunch_start", e.target.value)} />
                  <Input type="time" className="h-8 w-28" value={form.days[k].lunch_end || ""} onChange={e => setDay(k, "lunch_end", e.target.value)} />
                </>}
              </div>
            ))}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="shift-save-button" className="bg-slate-900">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
